/* import __COLOCATED_TEMPLATE__ from './edit-business.hbs'; */
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { computed, action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { ApplicationFlowUpdate } from '@square/dashboard-protos/models/squareup/sfs/external/models/application_flow_update';
import { Address } from '@square/dashboard-protos/models/squareup/connect/v2/resources/address';
import { later } from '@ember/runloop';
import { scrollToFirstError } from '@square/ember-prism/utils/error-scrolling';
import STATE_CODES from '../utils/state-codes';
import { PO_BOX_REGEX, POSTAL_CODE_REGEX, EMOJI_REGEX } from '../utils/regexes';

export default class EditBusinessComponent extends Component {
  @service intl;
  @service addressValidation;
  @service currentApplicationFlow;

  @tracked isLoading = false;
  @tracked showValidations = false;

  @tracked businessName = '';

  @tracked useSameAddress = false;
  @tracked line1 = '';
  @tracked line2 = '';
  @tracked city = '';
  @tracked state = null;
  @tracked zip = null;

  @tracked gotLine2Input = false;

  // FIXME: in Octane, this should be done from this.args.business in constructor()?
  didReceiveAttrs() {
    super.didReceiveAttrs();
    const business = this.business;
    if (business) {
      this.businessName = (business.businessName || '').trim();
      this.fillInBusinessAddress();

      if (this.addressValidation.businessFailedValidation) {
        this.showValidations = true;
        this.scrollToError();
      }
    }
  }

  @computed(
    'businessName.includes',
    'businessNameValidation',
    'city',
    'firstName',
    'isZipValid',
    'line1',
    'line1Validation',
    'line2',
    'line2Validation',
    'state',
    'zip'
  )
  get validations() {
    return {
      businessName: this.businessNameValidation,
      line1: this.line1Validation,
      line2: this.line2Validation,

      city: {
        isInvalid: this.city.trim() === '',
        message: this.intl.t('sfs.components.shared.validation.city'),
      },
      state: {
        isInvalid: !this.state,
        message: this.intl.t('sfs.components.shared.validation.state'),
      },
      zip: {
        isInvalid: !this.isZipValid,
        message: this.intl.t('sfs.components.shared.validation.zip'),
      },
    };
  }

  get isValid() {
    return Object.values(this.validations).every(
      ({ isInvalid, allowSubmit }) => allowSubmit || !isInvalid
    );
  }

  @computed(
    'addressValidation.businessFailedFullAddressValidation',
    'isLoading'
  )
  get showFullAddressValidation() {
    return (
      this.addressValidation.businessFailedFullAddressValidation &&
      !this.isLoading
    );
  }

  get isZipValid() {
    return this.zip && this.zip.trim().match(POSTAL_CODE_REGEX);
  }

  get businessNameValidation() {
    if (this.businessName?.trim() === '') {
      return {
        isInvalid: true,
        message: this.intl.t(
          'sfs.components.editBusiness.validation.businessName'
        ),
      };
    } else if (EMOJI_REGEX.test(this.businessName)) {
      return {
        isInvalid: true,
        message: this.intl.t(
          'sfs.components.editBusiness.validation.businessNameEmoji'
        ),
      };
    } else {
      return {
        isInvalid: false,
      };
    }
  }

  get line1Validation() {
    if (PO_BOX_REGEX.test(this.line1.trim())) {
      return {
        isInvalid: true,
        message: this.intl.t('sfs.components.shared.validation.poBoxLine1'),
      };
    } else {
      return {
        isInvalid: this.line1.trim() === '',
        message: this.intl.t('sfs.components.shared.validation.line1'),
      };
    }
  }

  // Line2 requires frontend PO box validation which SHOULD block submit.
  // Line2 also displays validation messages from the backend about official address verification,
  // which should NOT block form submit.
  @computed(
    'line2',
    'addressValidation.businessFailedLine2Validation',
    'gotLine2Input',
    'isLoading'
  )
  get line2Validation() {
    if (PO_BOX_REGEX.test(this.line2.trim())) {
      return {
        isInvalid: true,
        message: this.intl.t('sfs.components.shared.validation.poBoxLine2'),
      };
    } else {
      return {
        isInvalid:
          this.addressValidation.businessFailedLine2Validation &&
          !this.gotLine2Input &&
          !this.isLoading,
        message: this.intl.t('sfs.components.shared.validation.validateLine2'),
        allowSubmit: true,
      };
    }
  }

  get stateOptions() {
    return STATE_CODES.map((stateCode) => {
      return { label: stateCode, value: stateCode };
    });
  }

  get newAddress() {
    return new Address.Builder()
      .addressLine1(this.line1)
      .addressLine2(this.line2)
      .locality(this.city)
      .administrativeDistrictLevel1(this.state)
      .postalCode(this.zip)
      .build();
  }

  get newBusiness() {
    const builder = new ApplicationFlowUpdate.BusinessUpdate.Builder()
      .businessName(this.businessName?.trim())
      .address(this.newAddress);

    return builder.build();
  }

  fillInBusinessAddress() {
    const business = this.business;

    // map from Connect v2 Address
    this.line1 = business?.address?.addressLine1 || '';
    this.line2 = business?.address?.addressLine2 || '';
    this.city = business?.address?.locality || '';
    this.state = business?.address?.administrativeDistrictLevel1 || null;
    this.zip = business?.address?.postalCode || null;
  }

  @action
  toggleUseSameAddress(toggle) {
    this.useSameAddress = toggle;

    if (this.useSameAddress) {
      this.line1 = this.individual.address.addressLine1;
      this.line2 = this.individual.address.addressLine2;
      this.city = this.individual.address.locality;
      this.state = this.individual.address.administrativeDistrictLevel1;
      this.zip = this.individual.address.postalCode;
    } else {
      this.fillInBusinessAddress();
    }
  }

  @action
  line2Input() {
    this.gotLine2Input = true;
  }

  scrollToError() {
    // This is in a runloop because the error classes have not yet rendered
    later(() => {
      // Tests can remove the elements before `later` fires
      const modal = document.querySelector('.sfs-edit-pii__modal');
      if (modal) {
        scrollToFirstError(modal);
      }
    });
  }

  // The magic of Ember binds this automatically to the form's submit event
  async submit(event) {
    event.preventDefault();
    this.showValidations = true;

    if (this.isValid) {
      this.isLoading = true;
      try {
        await this.onUpdateBusiness(this.newBusiness);
      } finally {
        this.isLoading = false;
      }
    } else {
      this.scrollToError();
    }
  }
}
